import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { BRAND, NAV_LINKS } from "../lib/constants";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#050814]/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-2 group"
        >
          <div className="w-9 h-9 rounded-full border border-[#d4af37]/60 flex items-center justify-center bg-[#0a0f1d] group-hover:bg-[#d4af37]/10 transition-colors">
            <span className="font-display text-[#d4af37] text-lg leading-none">
              E
            </span>
          </div>
          <span className="font-display text-white text-lg tracking-tight">
            {BRAND.name}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative font-body text-sm tracking-wide transition-colors duration-300 ${
                  isActive
                    ? "text-[#d4af37]"
                    : "text-[#a0abc0] hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <span className="inline-flex flex-col items-start">
                  {l.label}
                  <span
                    className={`mt-1 block h-px bg-[#d4af37] transition-all duration-500 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link
            to="/contact"
            data-testid="header-cta"
            className="inline-flex items-center gap-2 bg-[#d4af37] hover:bg-[#f2e3c6] text-[#050814] font-semibold text-sm px-6 py-3 rounded-full transition-colors duration-300"
          >
            Start a project
            <span aria-hidden>→</span>
          </Link>
        </div>

        <button
          aria-label="Open menu"
          data-testid="mobile-menu-button"
          onClick={() => setOpen((s) => !s)}
          className="md:hidden text-white p-2"
        >
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#050814]/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-2xl ${
                      isActive ? "text-[#d4af37]" : "text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta"
                className="mt-4 inline-flex items-center justify-center bg-[#d4af37] text-[#050814] font-semibold text-sm px-6 py-3 rounded-full"
              >
                Start a project →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
